import { Suspense, type ReactNode } from 'react';
import { BrowserRouter, HashRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccess } from '../contexts/AccessContext';
import AppShell from '../components/AppShell';
import TeamLioExpenseShell from '../components/TeamLioExpenseShell';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import { TaskDetailRoute } from './TaskDetailRoute';
import { isSharedMeetlyPath } from '../hrms/utils/meetlyShare';
import {
  accessPathForLimitedWorkspaceRoute,
  canAccessLio,
  isIndependentMessengerPath,
  isLimitedWorkspace,
  isLimitedWorkspacePath,
  LIMITED_HOME_PATH,
} from '../utils/limitedAccess';

const isFileProtocol =
  typeof window !== 'undefined' && window.location.protocol === 'file:';
const Router = isFileProtocol ? HashRouter : BrowserRouter;

const Chat = lazyWithRetry(() => import('../pages/Chat/Chat'));
const ChatAudit = lazyWithRetry(() => import('../pages/ChatAudit/ChatAudit'));
const Calls = lazyWithRetry(() => import('../pages/Calls/Calls'));
const Dbms = lazyWithRetry(() => import('../pages/Dbms/Dbms'));
const Login = lazyWithRetry(() => import('../pages/Login'));
// Shared attendance kiosk. Deliberately NOT under ProtectedShell: a kiosk
// session is not a user session, so the shell's `user` would be null and it
// would bounce to /login forever. Rendering it as its own top-level route is
// also what makes "attendance and nothing else" structural — the sidebar and
// every other page simply never mount.
const KioskLogin = lazyWithRetry(() => import('../pages/Kiosk/KioskLogin'));
const KioskAttendance = lazyWithRetry(() => import('../pages/Kiosk/KioskAttendance'));
const Register = lazyWithRetry(() =>
  import('../pages/Chat/Register').then((m) => ({ default: m.Register }))
);

// Ported HRMS Task pages
const TaskList = lazyWithRetry(() => import('../hrms/pages/Task/TaskList'));
const TaskCreate = lazyWithRetry(() => import('../hrms/pages/Task/TaskCreatePage'));
const TaskEdit = lazyWithRetry(() => import('../hrms/pages/Task/TaskEditPage'));
const TaskDetails = lazyWithRetry(() => import('../hrms/pages/Task/TaskDetails'));
const RecentlyDeleted = lazyWithRetry(() => import('../hrms/pages/Task/RecentlyDeleted'));
const PartnerTaskDetail = lazyWithRetry(() => import('../hrms/pages/Task/PartnerTaskDetail'));
const MyTasks = lazyWithRetry(() => import('../hrms/pages/MyTasks/MyTasks'));
const TeamTasks = lazyWithRetry(() => import('../hrms/pages/TeamTasks/TeamTasks'));
const TaskDashboard = lazyWithRetry(() => import('../hrms/pages/TaskDashboard/TaskDashboard'));
const EmployeeDetails = lazyWithRetry(() => import('../hrms/pages/TeamVisibility/TeamVisibility'));
const TaskReports = lazyWithRetry(() => import('../hrms/pages/TaskReports'));
const TaskCodeSettings = lazyWithRetry(() => import('../hrms/pages/Settings/TaskCodeSettings'));
const AttendanceKiosks = lazyWithRetry(() => import('../hrms/pages/Settings/AttendanceKiosks'));
// Ported HRMS Calendar page
const CalendarPage = lazyWithRetry(() => import('../hrms/pages/Calendar/Calendar'));
const MeetlyWorkspace = lazyWithRetry(() => import('../hrms/pages/Meetly/MeetlyWorkspace'));
const MeetlyUsageSettings = lazyWithRetry(() => import('../hrms/pages/Meetly/MeetlyUsageSettings'));
const Leo = lazyWithRetry(() => import('../pages/Leo/Leo'));
const MyLioIssues = lazyWithRetry(() => import('../hrms/pages/MyLioIssues'));

// Daily Planner (Todo)
const TodoPlanner = lazyWithRetry(() => import('../hrms/pages/Todo/TodoPlanner'));
const TodoHistory = lazyWithRetry(() => import('../hrms/pages/Todo/TodoHistory'));
// Notemaker (personal notes) — mirrored from hrpayroll/src/pages/Notemaker
const Notemaker = lazyWithRetry(() => import('../hrms/pages/Notemaker/Notemaker'));

// Employee Hub
const Attendance = lazyWithRetry(() => import('../hrms/pages/Attendance'));
const Leaves = lazyWithRetry(() =>
  import('../hrms/pages/Leaves').then((m) => ({ default: m.Leaves }))
);
const Expenses = lazyWithRetry(() =>
  import('../hrms/pages/Expenses').then((m) => ({ default: m.Expenses }))
);
const Assets = lazyWithRetry(() => import('../hrms/pages/Assets/Assets'));
const Visitors = lazyWithRetry(() => import('../hrms/pages/Visitors/Visitors'));
// Feedback & Complaints — mirrored from hrpayroll/src/pages/Grievances
const Grievances = lazyWithRetry(() => import('../hrms/pages/Grievances/Grievances'));
const MyPayroll = lazyWithRetry(() => import('../hrms/pages/Payroll/MyPayroll'));
const PayrollApproval = lazyWithRetry(() => import('../hrms/pages/Payroll/PayrollApproval'));
const SalaryTaxDeclaration = lazyWithRetry(() => import('../hrms/pages/Payroll/SalaryTaxDeclaration'));
const PayrollReports = lazyWithRetry(() => import('../hrms/pages/Payroll/PayrollReports'));
const WorkSchedule = lazyWithRetry(() => import('../hrms/pages/Workflow/WorkSchedule'));
const WorkScheduleDetail = lazyWithRetry(() => import('../hrms/pages/Workflow/WorkScheduleDetail'));
const ActivityReports = lazyWithRetry(() => import('../hrms/pages/Workflow/ActivityReports'));
const ActivityComparison = lazyWithRetry(() => import('../hrms/pages/Workflow/ActivityComparison'));
const WorkflowRouteGuard = lazyWithRetry(() => import('../hrms/pages/Workflow/WorkflowRouteGuard'));

// Projects
const ProjectsPage = lazyWithRetry(() => import('../hrms/pages/Project/ProjectsPage'));
const ProjectDetails = lazyWithRetry(() => import('../hrms/pages/Project/ProjectDetails'));
const ProjectsRecentlyDeleted = lazyWithRetry(() => import('../hrms/pages/Project/ProjectsRecentlyDeleted'));

// Clients
const ClientsPage = lazyWithRetry(() => import('../hrms/pages/Client/ClientsPage'));

// Vendors
const VendorsPage = lazyWithRetry(() => import('../hrms/pages/Vendor/VendorsPage'));

// Login sessions — every user's own active devices, plus an admin audit page
const ActiveDevices = lazyWithRetry(() => import('../hrms/pages/ActiveDevices'));
const LoginSessions = lazyWithRetry(() => import('../hrms/pages/LoginSessions'));

function ProtectedShell() {
  const { user } = useAuth();
  const { loading, hasAccess, isPathVisible } = useAccess();
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace />;
  const limitedWorkspace = isLimitedWorkspace(user.subscription);
  if (limitedWorkspace && !isLimitedWorkspacePath(location.pathname)) {
    return <Navigate to={LIMITED_HOME_PATH} replace />;
  }
  if (
    location.pathname === '/leo' &&
    !loading &&
    !canAccessLio(user.role, hasAccess, isPathVisible('/leo'))
  ) {
    return <Navigate to={LIMITED_HOME_PATH} replace />;
  }
  // Access-profile gating: a user with an assigned profile can't open task
  // panels it doesn't enable. Messenger-native routes stay open, Lio has its
  // own role/access rule above, and a meeting shared with me is readable
  // without Meetly (the API guards it). Render through while permissions load.
  const bypassPanelAccess =
    isIndependentMessengerPath(location.pathname) ||
    location.pathname === '/leo' ||
    isSharedMeetlyPath(location.pathname);
  const accessPath = limitedWorkspace
    ? accessPathForLimitedWorkspaceRoute(location.pathname)
    : location.pathname;
  if (
    !bypassPanelAccess &&
    !loading &&
    hasAccess &&
    accessPath &&
    !isPathVisible(accessPath)
  ) {
    if (limitedWorkspace) {
      const firstVisibleLimitedPath = [
        LIMITED_HOME_PATH,
        '/tasks/list',
        '/employee/attendance',
        '/employee/leaves',
      ].find(isPathVisible);
      if (firstVisibleLimitedPath && firstVisibleLimitedPath !== location.pathname) {
        return <Navigate to={firstVisibleLimitedPath} replace />;
      }
    } else {
      return <Navigate to="/chat" replace />;
    }
  }
  return <AppShell />;
}

export function ProtectedExpenseShellRoute({ children }: { children?: ReactNode }) {
  const { user } = useAuth();
  const { loading, hasAccess, isPathVisible } = useAccess();

  if (!user) return <Navigate to="/login" replace />;
  if (isLimitedWorkspace(user.subscription)) {
    return <Navigate to={LIMITED_HOME_PATH} replace />;
  }
  if (!loading && hasAccess && !isPathVisible('/expenses')) {
    return <Navigate to="/chat" replace />;
  }

  return (
    <TeamLioExpenseShell>
      {children || (
        <Suspense fallback={<div className="p-8 text-sm text-gray-500">Loading...</div>}>
          <Expenses />
        </Suspense>
      )}
    </TeamLioExpenseShell>
  );
}

export function ProtectedAssetsShellRoute({ children }: { children?: ReactNode }) {
  const { user } = useAuth();
  const { loading, hasAccess, isPathVisible } = useAccess();

  if (!user) return <Navigate to="/login" replace />;
  if (isLimitedWorkspace(user.subscription)) {
    return <Navigate to={LIMITED_HOME_PATH} replace />;
  }
  if (!loading && hasAccess && !isPathVisible('/assets')) {
    return <Navigate to="/chat" replace />;
  }

  return (
    <TeamLioExpenseShell>
      {children || (
        <Suspense fallback={<div className="p-8 text-sm text-gray-500">Loading...</div>}>
          <Assets />
        </Suspense>
      )}
    </TeamLioExpenseShell>
  );
}

function DefaultRedirect() {
  const { user } = useAuth();
  return (
    <Navigate
      to={user && isLimitedWorkspace(user.subscription) ? LIMITED_HOME_PATH : '/chat'}
      replace
    />
  );
}

export default function AppRoutes() {
  return (
    <Router>
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              height: '100vh',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#0f766e',
              color: '#ffffff',
              fontFamily: 'system-ui, sans-serif',
              fontSize: 14,
            }}
          >
            Loading…
          </div>
        }
      >
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/kiosk" element={<KioskLogin />} />
          <Route path="/kiosk/attendance" element={<KioskAttendance />} />
          <Route path="/register" element={<Register />} />
          <Route path="/expenses" element={<ProtectedExpenseShellRoute />} />
          <Route path="/assets" element={<ProtectedAssetsShellRoute />} />
          <Route element={<ProtectedShell />}>
            <Route path="/chat" element={<Chat />} />
            <Route path="/chat-audit" element={<ChatAudit />} />
            <Route path="/calls" element={<Calls />} />
            <Route path="/dbms" element={<Dbms />} />

            {/* Employee Hub */}
            <Route path="/employee" element={<Navigate to="/employee/attendance" replace />} />
            <Route path="/employee/attendance" element={<Attendance />} />
            <Route path="/employee/attendance/kiosks" element={<AttendanceKiosks />} />
            <Route path="/employee/leaves" element={<Leaves />} />
            <Route path="/employee/expenses" element={<Expenses />} />
            <Route path="/employee/assets" element={<Assets />} />
            <Route path="/employee/visitors" element={<Visitors />} />
            <Route path="/employee/grievances" element={<Grievances />} />
            {/* Lio questions the superadmin handed to this person. Self-only
                server-side, so it is deliberately absent from accessPanels —
                there is no panel to grant and nothing to gate. */}
            <Route path="/employee/lio-questions" element={<MyLioIssues />} />
            <Route path="/employee/workflow/activity" element={<ActivityReports />} />
            <Route
              path="/employee/workflow/schedule"
              element={<WorkflowRouteGuard panel="work-schedule"><WorkSchedule /></WorkflowRouteGuard>}
            />
            <Route
              path="/employee/workflow/schedule/:employeeId"
              element={<WorkflowRouteGuard panel="work-schedule"><WorkScheduleDetail /></WorkflowRouteGuard>}
            />
            <Route
              path="/employee/workflow/comparison"
              element={<WorkflowRouteGuard panel="activity-comparison"><ActivityComparison /></WorkflowRouteGuard>}
            />
            <Route path="/employee/payroll" element={<MyPayroll />} />
            <Route path="/employee/payroll/tax" element={<SalaryTaxDeclaration />} />
            <Route path="/employee/payroll/approval" element={<PayrollApproval />} />
            <Route path="/employee/payroll/reports" element={<PayrollReports />} />
            <Route path="/attendance" element={<Navigate to="/employee/attendance" replace />} />
            <Route path="/leaves" element={<Navigate to="/employee/leaves" replace />} />
            <Route path="/my-payroll" element={<Navigate to="/employee/payroll" replace />} />
            <Route path="/salary-tax-declaration" element={<Navigate to="/employee/payroll/tax" replace />} />
            <Route path="/approval-settings" element={<Navigate to="/employee/payroll/approval" replace />} />
            <Route path="/payroll/reports" element={<Navigate to="/employee/payroll/reports" replace />} />

            {/* Task Management — 5 sections */}
            <Route path="/todo" element={<TodoPlanner />} />
            <Route path="/todo/history" element={<TodoHistory />} />
            {/* Notemaker — the optional :noteId deep-links one note. */}
            <Route path="/notes" element={<Notemaker />} />
            <Route path="/notes/:noteId" element={<Notemaker />} />

            {/* Meetly. The detail route carries the meeting id so a desktop
                window reopened mid-processing lands back on the right meeting. */}
            <Route path="/meetly" element={<MeetlyWorkspace />} />
            {/* Meetings colleagues shared with me (read-only). */}
            <Route path="/meetly/shared" element={<MeetlyWorkspace />} />
            <Route path="/meetly/meetings/:meetingId" element={<MeetlyWorkspace />} />
            <Route path="/meetly/usage" element={<MeetlyUsageSettings />} />

            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:id" element={<ProjectDetails />} />
            <Route path="/projects/recently-deleted" element={<ProjectsRecentlyDeleted />} />

            <Route path="/tasks" element={<Navigate to="/tasks/list" replace />} />
            <Route path="/tasks/list" element={<TaskList />} />
            <Route path="/tasks/create" element={<TaskCreate />} />
            <Route path="/tasks/dashboard" element={<TaskDashboard />} />
            <Route path="/tasks/employee-tasks" element={<EmployeeDetails />} />
            <Route path="/tasks/my-tasks" element={<MyTasks />} />
            <Route path="/tasks/team-tasks" element={<TeamTasks />} />
            <Route path="/tasks/recently-deleted" element={<RecentlyDeleted />} />
            <Route path="/tasks/reports" element={<TaskReports />} />
            <Route path="/tasks/code-settings" element={<TaskCodeSettings />} />
            <Route path="/tasks/:id/edit" element={<TaskEdit />} />
            <Route
              path="/tasks/:id"
              element={
                <TaskDetailRoute>
                  <TaskDetails />
                </TaskDetailRoute>
              }
            />
            <Route path="/partner-task/:id" element={<PartnerTaskDetail />} />

            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/vendors" element={<VendorsPage />} />

            {/* Every user's own live sessions — not gated by any Access panel. */}
            <Route path="/active-devices" element={<ActiveDevices />} />
            {/* Admin audit over everyone's session history — gated by the
                `login_sessions` panel via accessPanels.ts, same as
                /tasks/reports and the other catalog-backed pages above. */}
            <Route path="/login-sessions" element={<LoginSessions />} />

            {/* Leo — AI assistant */}
            <Route path="/leo" element={<Leo />} />

            {/* Calendar */}
            <Route path="/calendar" element={<CalendarPage />} />
          </Route>
          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
      </Suspense>
    </Router>
  );
}
